import React, { useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaFileAlt, FaFileExcel, FaFileImage, FaFilePdf, FaFileWord, FaPaperclip, FaTimes } from 'react-icons/fa';
import VisualizarArquivoModal from './VisualizarArquivoModal';
import '../css/financeiro-parcela-pagar.css';

/**
 * Campo de observação que aceita anexos inline, como o corpo de um e-mail: o print colado entra no ponto do cursor,
 * dentro do texto, e se arrasta dentro dele para mudar de lugar; Delete apaga. Os outros arquivos ficam no rodapé.
 * Anexa colando (Ctrl+V de um print), arrastando sobre o campo ou pelo botão Anexar; depois de colar, o cursor vai para
 * a linha seguinte ao anexo. O Enter quebra a linha sempre, mesmo dentro de um form que pula de campo com ele. A imagem
 * abre na prévia de arquivos (VisualizarArquivoModal): com um clique na leitura, com dois editando. A imagem gravada
 * mostra um bloco animado até chegar; a que não chega fica com o nome do arquivo.
 *
 * O campo é um `contenteditable`, o editável nativo do HTML; o que sai dele não é HTML. Controlado: `value` =
 * { texto, anexos } e `onChange` devolve o value inteiro, lido do campo a cada mudança.
 * - `texto`: a observação, só texto; as imagens não entram nele.
 * - `anexos`: a lista, na ordem em que aparecem. Cada anexo é uma entidade com o arquivo e a linha:
 *     novo:  { id, arquivo, linha } — `arquivo` é o File do navegador, sem leitura nem conversão, e o `id` é provisório
 *            (1, 2, 3...) até o back gravar; quem salva manda por multipart (appendToFormData), nunca o binário no JSON
 *     salvo: { id, nome, link, linha } — o id real, com o link do arquivo gravado
 *   `linha`: o anexo aparece depois de tantas linhas do texto (0 = antes da primeira); `null` = sem posição, depois do
 *   texto. Cada anexo ocupa uma linha inteira: solto no meio de uma linha, ele fica depois dela. Anexos da mesma
 *   linha seguem a ordem da lista, que vai para o back como `ordem`.
 *
 * `anexosNoCorpo`: quais anexos ficam no texto; os outros, no rodapé.
 *   'imagens' (padrão) — as imagens, como num e-mail; os demais arquivos no rodapé
 *   'todos'            — imagens e arquivos, estes como etiqueta
 *   'nenhum'           — tudo no rodapé, como anexo de e-mail
 * `maxAnexos`: limite da lista; cheio, o anexo novo não entra e um aviso explica.
 * `readOnly`: a leitura, com a aparência normal do campo; `disabled`: a leitura com a aparência de desabilitado.
 * `semCaixa`: sem borda, fundo nem altura mínima, como o corpo de um e-mail; vale para editar e para a leitura.
 *
 *   <FinField label="Observação" full>
 *     <ObservacaoAnexos value={obs} onChange={setObs} />
 *   </FinField>
 */

const EXTENSOES_IMAGEM = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

const arquivoDe = (anexo) => (anexo?.arquivo instanceof File ? anexo.arquivo : null);

export const nomeDoAnexo = (anexo) => arquivoDe(anexo)?.name || anexo?.nome || 'arquivo';

export const ehImagem = (anexo) => {
    const arquivo = arquivoDe(anexo);
    return arquivo ? (arquivo.type || '').startsWith('image/') : EXTENSOES_IMAGEM.test(nomeDoAnexo(anexo));
};

// O print colado chega do navegador como "image.png": vira "print-<data>-<hora>" para os nomes não se repetirem.
// Arquivo copiado do Explorer chega com o nome dele e fica como está.
export const nomearColado = (arquivo, agora = new Date()) => {
    if (!/^image\.(png|jpe?g|gif|webp)$/i.test(arquivo.name)) return arquivo;
    const dois = (n) => String(n).padStart(2, '0');
    const data = `${agora.getFullYear()}${dois(agora.getMonth() + 1)}${dois(agora.getDate())}`;
    const hora = `${dois(agora.getHours())}${dois(agora.getMinutes())}${dois(agora.getSeconds())}`;
    const extensao = arquivo.name.split('.').pop();
    return new File([arquivo], `print-${data}-${hora}.${extensao}`, { type: arquivo.type, lastModified: arquivo.lastModified });
};

// O mesmo filtro do `accept` do input de arquivo, para o arrastar e o colar, que passam por fora dele.
export const aceita = (arquivo, accept) => {
    if (!accept) return true;
    const nome = (arquivo.name || '').toLowerCase();
    const tipo = (arquivo.type || '').toLowerCase();
    return accept.split(',').map((regra) => regra.trim().toLowerCase()).filter(Boolean).some((regra) => {
        if (regra.startsWith('.')) return nome.endsWith(regra);
        if (regra.endsWith('/*')) return tipo.startsWith(regra.slice(0, -1));
        return tipo === regra;
    });
};

const vaiNoCorpo = (anexo, anexosNoCorpo) => anexosNoCorpo === 'todos' || (anexosNoCorpo === 'imagens' && ehImagem(anexo));

// Lê o campo editável: o texto, linha a linha, e a linha de cada anexo, na ordem em que aparecem. Cada navegador
// monta as linhas do seu jeito (div por linha, <br>, texto solto na raiz), e a leitura aceita todos. Anexo no meio de
// uma linha fica depois dela, para o texto não ser cortado. `chavePorSrc` reconhece a imagem que o navegador recriou
// ao arrastar, se ela perder o data-anexo.
export const lerEditor = (raiz, chavePorSrc = new Map()) => {
    const linhas = [];
    const posicoes = [];
    let atual = null;
    const fechar = () => {
        if (atual !== null) linhas.push(atual);
        atual = null;
    };
    const visitar = (no) => {
        if (no.nodeType === 3) {
            atual = (atual ?? '') + no.nodeValue;
            return;
        }
        if (no.nodeType !== 1) return;
        const chave = no.dataset?.anexo || (no.tagName === 'IMG' ? chavePorSrc.get(no.getAttribute('src')) : null);
        if (chave) {
            posicoes.push({ chave, linha: atual ? linhas.length + 1 : linhas.length });
            return;
        }
        if (no.tagName === 'BR') {
            linhas.push(atual ?? '');
            atual = null;
            return;
        }
        const bloco = /^(DIV|P)$/.test(no.tagName);
        if (bloco) fechar();
        no.childNodes.forEach(visitar);
        if (bloco) fechar();
    };
    raiz.childNodes.forEach(visitar);
    fechar();
    return { texto: linhas.join('\n'), posicoes };
};

const tamanhoLegivel = (bytes) => {
    if (typeof bytes !== 'number') return null;
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

const iconeDoAnexo = (anexo) => {
    if (ehImagem(anexo)) return FaFileImage;
    const nome = nomeDoAnexo(anexo).toLowerCase();
    if (nome.endsWith('.pdf')) return FaFilePdf;
    if (/\.(xlsx?|xlsb|csv)$/.test(nome)) return FaFileExcel;
    if (/\.docx?$/.test(nome)) return FaFileWord;
    return FaFileAlt;
};

export default function ObservacaoAnexos({
    value = null,
    onChange = null,
    anexosNoCorpo = 'imagens',
    placeholder = 'Escreva a observação',
    accept = null,
    maxAnexos = null,
    disabled = false,
    readOnly = false,
    semCaixa = false,
}) {
    const editorRef = useRef(null);
    const inputRef = useRef(null);
    const urlsRef = useRef(new Map());
    const emitidoRef = useRef(null);
    const pontoGuardadoRef = useRef(null);
    const arrastoInternoRef = useRef(false);
    const [arrastando, setArrastando] = useState(false);
    const [previa, setPrevia] = useState(null);
    const texto = value?.texto ?? '';
    const anexos = value?.anexos || [];
    const editavel = !disabled && !readOnly;

    // Prévia dos File novos por URL temporária do navegador, sem ler o arquivo. O cache acompanha a lista: cria a URL
    // do File que entrou e libera a do que saiu. Fica no render, e não num efeito com estado, porque a lista pode
    // chegar nova a cada render (value={{ ... }}) e o efeito entraria em laço.
    const arquivosNovos = anexos.map(arquivoDe).filter(Boolean);
    urlsRef.current.forEach((url, arquivo) => {
        if (!arquivosNovos.includes(arquivo)) {
            URL.revokeObjectURL(url);
            urlsRef.current.delete(arquivo);
        }
    });
    const urlDe = (arquivo) => {
        if (!urlsRef.current.has(arquivo)) urlsRef.current.set(arquivo, URL.createObjectURL(arquivo));
        return urlsRef.current.get(arquivo);
    };
    arquivosNovos.forEach(urlDe);

    const linkDe = (anexo) => (arquivoDe(anexo) ? urlDe(arquivoDe(anexo)) : (anexo?.link || anexo?.url));
    const chaveDe = (anexo) => `${arquivoDe(anexo) ? 'novo' : 'salvo'}-${anexo.id}`;
    const corpo = anexos.filter((anexo) => vaiNoCorpo(anexo, anexosNoCorpo));
    const rodape = anexos.filter((anexo) => !vaiNoCorpo(anexo, anexosNoCorpo));
    const assinatura = (textoDe, corpoDe) => `${textoDe}\n--\n${corpoDe.map((anexo) => `${chaveDe(anexo)}:${anexo.linha ?? ''}`).join(',')}`;

    // O anexo dentro do campo: a imagem, ou a etiqueta do arquivo (no modo 'todos'), numa linha só dela.
    const blocoDoAnexo = (anexo) => {
        const linha = document.createElement('div');
        let elemento;
        if (ehImagem(anexo)) {
            elemento = document.createElement('img');
            // A imagem gravada vem da API e demora (um print tem MB): até chegar, um bloco animado no lugar dela, e não
            // o nome do arquivo, que o navegador mostra no lugar da imagem que ainda não chegou. A que não chega fica
            // com o nome. A do arquivo novo é do próprio navegador e aparece na hora.
            if (!arquivoDe(anexo)) {
                elemento.className = 'is-carregando';
                elemento.addEventListener('load', () => elemento.classList.remove('is-carregando'));
                elemento.addEventListener('error', () => elemento.classList.replace('is-carregando', 'is-erro'));
            }
            elemento.src = linkDe(anexo) || '';
            elemento.alt = nomeDoAnexo(anexo);
            elemento.title = editavel
                ? `${nomeDoAnexo(anexo)}: arraste para mudar de lugar; dois cliques para ver; selecione e aperte Delete para tirar`
                : `${nomeDoAnexo(anexo)}: clique para ver`;
        } else {
            elemento = document.createElement('span');
            elemento.contentEditable = 'false';
            elemento.className = 'fin-pp__obs-anexo';
            elemento.textContent = nomeDoAnexo(anexo);
        }
        elemento.dataset.anexo = chaveDe(anexo);
        linha.appendChild(elemento);
        return linha;
    };

    // Monta o campo a partir do value: as linhas do texto e, entre elas, os anexos nas linhas deles.
    const montarEditor = (textoDe, corpoDe) => {
        const editor = editorRef.current;
        const linhasDoTexto = textoDe === '' ? [] : textoDe.split('\n');
        editor.innerHTML = '';
        for (let n = 0; n <= linhasDoTexto.length; n += 1) {
            const fim = n === linhasDoTexto.length;
            corpoDe
                .filter((anexo) => {
                    const linha = anexo.linha ?? linhasDoTexto.length;
                    return fim ? linha >= n : linha === n;
                })
                .forEach((anexo) => editor.appendChild(blocoDoAnexo(anexo)));
            if (!fim) {
                const linha = document.createElement('div');
                if (linhasDoTexto[n]) linha.textContent = linhasDoTexto[n];
                else linha.appendChild(document.createElement('br'));
                editor.appendChild(linha);
            }
        }
    };

    // O campo só é remontado quando o value chega de fora (abrir, trocar de registro); o que veio do próprio campo já
    // está nele, e remontar tiraria o cursor do lugar.
    useLayoutEffect(() => {
        if (!editorRef.current) return;
        const chegou = `${assinatura(texto, corpo)}\n${editavel}`;
        if (chegou === emitidoRef.current) return;
        montarEditor(texto, corpo);
        emitidoRef.current = chegou;
    });

    // Lê o campo e devolve o value: os anexos do texto na ordem em que aparecem, e os do rodapé. Com `remontar`, o campo
    // é remontado a partir do value devolvido, e a imagem que o navegador soltou no meio de uma linha volta a ter a sua.
    const emitir = (novos = [], remontar = false) => {
        const porChave = new Map([...anexos, ...novos].map((anexo) => [chaveDe(anexo), anexo]));
        const chavePorSrc = new Map([...anexos, ...novos].filter((anexo) => ehImagem(anexo)).map((anexo) => [linkDe(anexo), chaveDe(anexo)]));
        const lido = lerEditor(editorRef.current, chavePorSrc);
        const noTexto = lido.posicoes
            .filter(({ chave }, i, todos) => porChave.has(chave) && todos.findIndex((outro) => outro.chave === chave) === i)
            .map(({ chave, linha }) => ({ ...porChave.get(chave), linha }));
        const foraDoTexto = [...rodape, ...novos.filter((anexo) => !vaiNoCorpo(anexo, anexosNoCorpo))];
        emitidoRef.current = remontar ? null : `${assinatura(lido.texto, noTexto)}\n${editavel}`;
        if (onChange) onChange({ texto: lido.texto, anexos: [...noTexto, ...foraDoTexto] });
    };

    // A linha de topo do campo em que está um ponto; null = o fim.
    const blocoDe = (no, deslocamento) => {
        const editor = editorRef.current;
        if (!no || !editor.contains(no)) return null;
        if (no === editor) return editor.childNodes[deslocamento - 1] || null;
        let atual = no;
        while (atual.parentNode !== editor) atual = atual.parentNode;
        return atual;
    };
    // Onde o anexo entra, a partir de um ponto do campo (o cursor, ou onde o arquivo caiu): depois da linha do ponto, ou
    // antes dela quando nada vem antes do ponto na linha (a linha vazia, ou o começo dela, onde o cursor fica depois de
    // colar). Ponto entre duas linhas fica depois da de cima. `bloco` null = o fim do campo.
    const pontoDe = (range) => {
        const editor = editorRef.current;
        const bloco = blocoDe(range.endContainer, range.endOffset);
        if (!bloco || range.endContainer === editor) return { bloco, antes: false };
        const trecho = document.createRange();
        trecho.setStart(bloco, 0);
        trecho.setEnd(range.endContainer, range.endOffset);
        const conteudo = trecho.cloneContents();
        return { bloco, antes: conteudo.textContent === '' && !conteudo.querySelector('img, [data-anexo]') };
    };
    // O ponto do cursor, ou o guardado quando o campo perdeu o foco (o Anexar tira o foco dele).
    const pontoDoCursor = () => {
        const selecao = window.getSelection();
        if (selecao && selecao.rangeCount) {
            const range = selecao.getRangeAt(0);
            if (editorRef.current.contains(range.endContainer)) return pontoDe(range);
        }
        const guardado = pontoGuardadoRef.current;
        return guardado && editorRef.current.contains(guardado.bloco) ? guardado : { bloco: null, antes: false };
    };
    const guardarCursor = () => {
        const selecao = window.getSelection();
        if (selecao && selecao.rangeCount) {
            const range = selecao.getRangeAt(0);
            if (editorRef.current.contains(range.endContainer)) pontoGuardadoRef.current = pontoDe(range);
        }
    };

    // O campo tem altura máxima e rola: a linha que entrou fora da área visível é trazida para ela.
    const mostrar = (no) => {
        if (no && no.nodeType === 1 && typeof no.scrollIntoView === 'function') no.scrollIntoView({ block: 'nearest' });
    };

    // Põe os arquivos na lista e, os do texto, no campo, no ponto dado ou no do cursor. Devolve a linha do último anexo
    // posto no texto, ou null.
    const adicionar = (arquivos, ponto = pontoDoCursor()) => {
        if (!editavel) return null;
        const recebidos = Array.from(arquivos ?? []);
        const aceitos = recebidos.filter((arquivo) => aceita(arquivo, accept));
        const recusados = recebidos.filter((arquivo) => !aceitos.includes(arquivo));
        if (recusados.length) toast.warning(`Este campo não aceita: ${recusados.map((arquivo) => arquivo.name).join(', ')}`);
        // Com limite cheio, o anexo novo não entra, e o aviso diz por quê.
        const cabem = maxAnexos ? Math.max(0, maxAnexos - anexos.length) : aceitos.length;
        const entram = aceitos.slice(0, cabem);
        if (entram.length < aceitos.length) {
            toast.warning(`Este campo aceita até ${maxAnexos} ${maxAnexos === 1 ? 'anexo' : 'anexos'}. Tire um para colocar outro.`);
        }
        if (!entram.length) return null;
        // Id provisório, em sequência entre os anexos novos, até o back gravar e devolver o id real.
        const proximo = Math.max(0, ...anexos.filter(arquivoDe).map((anexo) => Number(anexo.id) || 0)) + 1;
        const novos = entram.map((arquivo, i) => ({ id: proximo + i, arquivo, linha: null }));
        const editor = editorRef.current;
        const blocos = novos.filter((anexo) => vaiNoCorpo(anexo, anexosNoCorpo)).map(blocoDoAnexo);
        let ultimo = null;
        if (blocos.length) {
            if (ponto.bloco && editor.contains(ponto.bloco)) {
                if (ponto.antes) ponto.bloco.before(...blocos);
                else ponto.bloco.after(...blocos);
            } else {
                blocos.forEach((bloco) => editor.appendChild(bloco));
            }
            ultimo = blocos[blocos.length - 1];
            pontoGuardadoRef.current = { bloco: ultimo, antes: false };
            mostrar(ultimo);
        }
        emitir(novos);
        return ultimo;
    };

    // Depois de colar, o cursor vai para a linha seguinte ao anexo, para seguir escrevendo abaixo dele, como depois de
    // colar uma imagem num e-mail. O anexo no fim do campo ganha uma linha vazia depois dele, para o cursor ter onde ficar.
    const cursorDepoisDe = (bloco) => {
        const editor = editorRef.current;
        const selecao = window.getSelection();
        if (!bloco || !selecao || !editor.contains(bloco)) return;
        let seguinte = bloco.nextSibling;
        if (!seguinte) {
            seguinte = document.createElement('div');
            seguinte.appendChild(document.createElement('br'));
            editor.appendChild(seguinte);
        }
        const range = document.createRange();
        range.setStart(seguinte, 0);
        range.collapse(true);
        selecao.removeAllRanges();
        selecao.addRange(range);
        mostrar(seguinte);
    };

    const removerDoRodape = (anexo) => {
        if (onChange) onChange({ texto, anexos: anexos.filter((outro) => outro !== anexo) });
    };

    // Colar: o print vira anexo no ponto do cursor, e o cursor vai para a linha depois dele; texto entra sem a
    // formatação de onde veio.
    const colar = (e) => {
        if (!editavel) return;
        const arquivos = Array.from(e.clipboardData?.files || []);
        const tipos = Array.from(e.clipboardData?.types || []);
        e.preventDefault();
        if (arquivos.length && !tipos.includes('text/plain')) {
            cursorDepoisDe(adicionar(arquivos.map((arquivo) => nomearColado(arquivo))));
            return;
        }
        const textoColado = e.clipboardData?.getData('text/plain');
        if (textoColado && document.execCommand) document.execCommand('insertText', false, textoColado);
    };

    // Arrastar: a imagem arrastada dentro do campo muda de lugar pelo próprio navegador, e o campo é remontado no fim do
    // arrasto (comecarArrasto) para ela ocupar uma linha só dela. O arrasto interno é conferido antes do arquivo porque o
    // Chrome manda a imagem arrastada também como arquivo, e ela voltaria como anexo novo, duplicada. Arquivo de fora
    // vira anexo onde caiu; outro conteúdo de fora não entra.
    const temArquivo = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const arrastar = (e) => {
        if (!editavel || arrastoInternoRef.current) return;
        e.preventDefault();
        if (temArquivo(e)) setArrastando(true);
    };
    const sair = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setArrastando(false);
    };
    const soltar = (e) => {
        setArrastando(false);
        if (!editavel || arrastoInternoRef.current) return;
        e.preventDefault();
        if (temArquivo(e)) {
            const range = document.caretRangeFromPoint ? document.caretRangeFromPoint(e.clientX, e.clientY) : null;
            adicionar(e.dataTransfer.files, range ? pontoDe(range) : { bloco: null, antes: false });
        }
    };

    // O Chrome move a imagem arrastada em dois passos, tira e depois solta, com um input no meio. Esse input não pode
    // virar value: sem a imagem, o anexo sairia da lista, o link temporário dela seria liberado e ela voltaria quebrada
    // no lugar novo. Durante o arrasto o campo não emite, e o value sai uma vez só, no fim. O fim é pego na janela
    // porque a imagem movida sai do campo antes do dragend, e o evento dela não chega mais aqui.
    const digitar = () => {
        if (!arrastoInternoRef.current) emitir();
    };
    const comecarArrasto = () => {
        arrastoInternoRef.current = true;
        const terminar = () => {
            window.removeEventListener('drop', terminar, true);
            window.removeEventListener('dragend', terminar, true);
            setTimeout(() => {
                arrastoInternoRef.current = false;
                if (editorRef.current) emitir([], true);
            }, 0);
        };
        window.addEventListener('drop', terminar, true);
        window.addEventListener('dragend', terminar, true);
    };

    // A imagem abre na prévia de arquivos: na leitura com um clique, editando com dois, porque o clique simples
    // seleciona a imagem para arrastar ou apagar. A imagem que o navegador recriou ao arrastar é achada pelo src.
    const abrirPrevia = (e) => {
        if (e.target.tagName !== 'IMG') return;
        const chave = e.target.dataset.anexo;
        const anexo = anexos.find((outro) => (chave ? chaveDe(outro) === chave : linkDe(outro) === e.target.getAttribute('src')));
        if (anexo) setPrevia(anexo);
    };

    // O Enter quebra a linha aqui, como num textarea, e não sobe para a tela: o form que usa o Enter para pular de
    // campo (o do evento faz isso) cancelaria a linha nova.
    const manterEnter = (e) => {
        if (e.key === 'Enter') e.stopPropagation();
    };

    const escolher = (e) => {
        adicionar(e.target.files);
        e.target.value = '';
    };

    const etiqueta = (anexo) => {
        const nome = nomeDoAnexo(anexo);
        const link = linkDe(anexo);
        const Icone = iconeDoAnexo(anexo);
        const tamanho = arquivoDe(anexo) ? tamanhoLegivel(arquivoDe(anexo).size) : null;
        return (
            <span className="fin-pp__obs-anexo" key={chaveDe(anexo)}>
                <Icone size={13} className="fin-pp__obs-anexo-ic" />
                {link
                    ? <a href={link} target="_blank" rel="noopener noreferrer" title={nome}>{nome}</a>
                    : <span className="fin-pp__obs-anexo-nome" title={nome}>{nome}</span>}
                {tamanho ? <span className="fin-pp__obs-anexo-tam">{tamanho}</span> : null}
                {editavel ? (
                    <button type="button" className="fin-pp__obs-remover" onClick={() => removerDoRodape(anexo)} title={`Remover ${nome}`} aria-label={`Remover ${nome}`}>
                        <FaTimes size={10} />
                    </button>
                ) : null}
            </span>
        );
    };

    let dica = null;
    if (corpo.length) dica = 'Arraste a imagem para mudar de lugar; dois cliques para ver; selecione e aperte Delete para tirar';
    else if (!anexos.length) dica = 'Cole (Ctrl+V) ou arraste prints e arquivos no campo';

    return (
        <div
            className={'fin-pp fin-pp__obs' + (arrastando ? ' is-dragging' : '') + (disabled ? ' is-disabled' : '')
                + (editavel ? '' : ' is-leitura') + (semCaixa ? ' fin-pp__obs--sem-caixa' : '')}
            onDragEnter={arrastar}
            onDragOver={arrastar}
            onDragLeave={sair}
            onDrop={soltar}
        >
            <div
                ref={editorRef}
                className="fin-pp__obs-editor"
                contentEditable={editavel}
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                aria-readonly={!editavel}
                data-placeholder={editavel ? placeholder : 'Sem observação'}
                onInput={digitar}
                onPaste={colar}
                onClick={editavel ? undefined : abrirPrevia}
                onDoubleClick={editavel ? abrirPrevia : undefined}
                onKeyDown={manterEnter}
                onKeyUp={guardarCursor}
                onMouseUp={guardarCursor}
                onBlur={guardarCursor}
                onDragStart={editavel ? comecarArrasto : undefined}
            />
            {(rodape.length > 0 || editavel) && (
                <div className="fin-pp__obs-rodape">
                    {rodape.map(etiqueta)}
                    {editavel && (
                        <span className="fin-pp__obs-acoes">
                            {dica ? <span className="fin-pp__obs-dica">{dica}</span> : null}
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => inputRef.current?.click()}>
                                <FaPaperclip size={12} /> Anexar
                            </button>
                        </span>
                    )}
                </div>
            )}
            <input
                ref={inputRef}
                type="file"
                multiple={maxAnexos !== 1}
                accept={accept ?? undefined}
                disabled={!editavel}
                style={{ display: 'none' }}
                onChange={escolher}
            />
            <VisualizarArquivoModal
                show={!!previa}
                onHide={() => setPrevia(null)}
                nome={previa ? nomeDoAnexo(previa) : ''}
                localFile={arquivoDe(previa) || undefined}
                url={previa && !arquivoDe(previa) ? linkDe(previa) : undefined}
            />
        </div>
    );
}
